import { Monitor, Moon, Sun } from "lucide-react";
import { useTheme } from "next-themes";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

type ThemeToggleProps = {
  /** Extra classes for the icon trigger (e.g. on solid brand backgrounds). */
  triggerClassName?: string;
};

export function ThemeToggle({ triggerClassName }: ThemeToggleProps): JSX.Element {
  const { t } = useTranslation();
  const { theme, setTheme, resolvedTheme } = useTheme();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const activeIcon =
    !mounted ? (
      <Sun className="h-4 w-4" aria-hidden />
    ) : resolvedTheme === "dark" ? (
      <Moon className="h-4 w-4" aria-hidden />
    ) : (
      <Sun className="h-4 w-4" aria-hidden />
    );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className={cn(
            "shrink-0 transition-colors duration-150",
            triggerClassName
          )}
          aria-label={t("header.theme.label")}
        >
          {activeIcon}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[10rem]">
        <DropdownMenuItem
          className="cursor-pointer gap-2"
          onClick={() => setTheme("light")}
        >
          <Sun className="h-4 w-4" aria-hidden />
          {t("header.theme.light")}
          {mounted && theme === "light" ? (
            <span className="ml-auto text-primary">✓</span>
          ) : null}
        </DropdownMenuItem>
        <DropdownMenuItem
          className="cursor-pointer gap-2"
          onClick={() => setTheme("dark")}
        >
          <Moon className="h-4 w-4" aria-hidden />
          {t("header.theme.dark")}
          {mounted && theme === "dark" ? (
            <span className="ml-auto text-primary">✓</span>
          ) : null}
        </DropdownMenuItem>
        <DropdownMenuItem
          className="cursor-pointer gap-2"
          onClick={() => setTheme("system")}
        >
          <Monitor className="h-4 w-4" aria-hidden />
          {t("header.theme.system")}
          {mounted && theme === "system" ? (
            <span className="ml-auto text-primary">✓</span>
          ) : null}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** Theme Light / Dark / System as a nested submenu (e.g. sidebar account menu). */
export function ThemePreferenceSubmenu(): JSX.Element {
  const { t } = useTranslation();
  const { theme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger className="cursor-pointer gap-2">
        <span className="flex size-4 shrink-0 items-center justify-center">
          <Sun className="size-4" aria-hidden />
        </span>
        <span className="flex-1 text-left">{t("header.theme.label")}</span>
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent className="min-w-[10rem]" sideOffset={6}>
        <DropdownMenuItem
          className="cursor-pointer gap-2"
          onClick={() => setTheme("light")}
        >
          <Sun className="h-4 w-4" aria-hidden />
          {t("header.theme.light")}
          {mounted && theme === "light" ? (
            <span className="ml-auto text-primary">✓</span>
          ) : null}
        </DropdownMenuItem>
        <DropdownMenuItem
          className="cursor-pointer gap-2"
          onClick={() => setTheme("dark")}
        >
          <Moon className="h-4 w-4" aria-hidden />
          {t("header.theme.dark")}
          {mounted && theme === "dark" ? (
            <span className="ml-auto text-primary">✓</span>
          ) : null}
        </DropdownMenuItem>
        <DropdownMenuItem
          className="cursor-pointer gap-2"
          onClick={() => setTheme("system")}
        >
          <Monitor className="h-4 w-4" aria-hidden />
          {t("header.theme.system")}
          {mounted && theme === "system" ? (
            <span className="ml-auto text-primary">✓</span>
          ) : null}
        </DropdownMenuItem>
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  );
}
